// ════════════════════════════════════════════════════════════════════════
// LandingPage.tsx — Ensamblaje de todas las secciones de la landing
// ════════════════════════════════════════════════════════════════════════
// CONCEPTO: React.lazy + Suspense (Code Splitting)
// React.lazy carga los componentes en chunks separados de JavaScript.
// En lugar de que el usuario descargue TODO el JS al inicio,
// cada sección se descarga cuando está cerca de ser visible.
//
// BENEFICIO: El primer render es más rápido (menos JS que parsear).
// CloudFront cacheará cada chunk por separado con su propio hash.
//
// LIMITACIÓN: React.lazy solo funciona con default exports.
// Por eso todas las secciones usan "export default function Sección".
//
// Suspense — React esperará al lazy import. Mientras tanto muestra
// el fallback. El fallback es un componente simple de carga.
// ════════════════════════════════════════════════════════════════════════

import { lazy, Suspense } from 'react';
// Hero se importa de entrada (NO lazy) — es lo primero que debe verse al
// cargar la página. Con lazy(), cada <Suspense> se resuelve por su cuenta
// apenas su propio chunk termina de descargar, sin respetar el orden de
// la página — así, una sección más liviana más abajo podía terminar de
// cargar y mostrarse ANTES que el Hero, que mientras tanto solo mostraba
// un spinner chico (~200px) en su lugar. Resultado: en la primera pintura
// se veía sobre todo la sección de abajo, hasta que el Hero llegaba y
// empujaba todo hacia abajo. Al importarlo de forma normal, es parte del
// paquete inicial y siempre se pinta primero, con su tamaño real.
import Hero from '@/marketing/sections/Hero/Hero';

// ── Importaciones lazy — cada sección es un chunk separado ────────────
// NOTA: El nombre del chunk en el bundle está dado por el nombre del módulo.
// vite.config.ts puede usar manualChunks para agruparlos estratégicamente.
const ImpactStrip    = lazy(() => import('@/marketing/sections/ImpactStrip/ImpactStrip'));
const ServicesSection= lazy(() => import('@/marketing/sections/ServicesSection/ServicesSection'));
const RoadmapSection = lazy(() => import('@/marketing/sections/RoadmapSection/RoadmapSection'));
const ContrastSection= lazy(() => import('@/marketing/sections/ContrastSection/ContrastSection'));
const WhySection     = lazy(() => import('@/marketing/sections/WhySection/WhySection'));
const FAQSection     = lazy(() => import('@/marketing/sections/FAQSection/FAQSection'));
const FormSection    = lazy(() => import('@/marketing/sections/FormSection/FormSection'));
const CTASection     = lazy(() => import('@/marketing/sections/CTASection/CTASection'));

// ── Componente de loading para Suspense ───────────────────────────────
// Aparece brevemente mientras se descarga y parsea el chunk de la sección.
// Usando "skeleton" — estructura visual que se parece al contenido real.
function SectionFallback() {
  return (
    <div
      style={{
        padding: '4rem 2rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
      }}
      role="status"
      aria-label="Cargando sección..."
    >
      {/* Indicador de carga minimalista */}
      <div
        style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(0, 212, 255, 0.15)',
          borderTopColor: 'var(--brand-accent)',
          borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
        }}
        aria-hidden="true"
      />
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────
export default function LandingPage() {
  return (
    // <main> — elemento semántico que marca el contenido principal del documento.
    // Solo debe haber uno por página (accesibilidad).
    <main id="main-content">
      {/* Hero NO va envuelto en Suspense — se importa de forma normal
          (ver arriba), así que se renderiza de inmediato, siempre primero.
          Cada Suspense de abajo envuelve una sección lazy; si falla al
          cargar, el error boundary padre lo captura. El fallback se
          muestra mientras el lazy import resuelve. */}

      <Hero />

      <Suspense fallback={<SectionFallback />}>
        <ImpactStrip />
      </Suspense>

      <Suspense fallback={<SectionFallback />}>
        <ServicesSection />
      </Suspense>

      <Suspense fallback={<SectionFallback />}>
        <RoadmapSection />
      </Suspense>

      <Suspense fallback={<SectionFallback />}>
        <ContrastSection />
      </Suspense>

      <Suspense fallback={<SectionFallback />}>
        <WhySection />
      </Suspense>

      <Suspense fallback={<SectionFallback />}>
        <FAQSection />
      </Suspense>

      <Suspense fallback={<SectionFallback />}>
        <FormSection />
      </Suspense>

      <Suspense fallback={<SectionFallback />}>
        <CTASection />
      </Suspense>
    </main>
  );
}
